import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { LifeBuoy, Plus, Send, ArrowLeft } from "lucide-react";

export const NEW_CATS = [{ v: "consiglio", l: "Consigli" }, { v: "bug", l: "Bug" }, { v: "richiesta", l: "Richieste" }];
export const CATS = [
  ...NEW_CATS, { v: "problema", l: "Problema" }, { v: "segnalazione", l: "Segnalazione" }, { v: "suggerimento", l: "Suggerimento" },
];
export const catLabel = (v) => (CATS.find((c) => c.v === v)?.l || v);
export const statusLabel = (s) => ({ open: "Aperta", answered: "Risposta", closed: "Chiusa" }[s] || s);
export const statusCls = (s) => ({ open: "bg-amber-500/10 text-amber-700", answered: "bg-emerald-500/10 text-emerald-700", closed: "bg-neutral-500/10 text-neutral-600" }[s] || "");

export default function Tickets() {
  const [tickets, setTickets] = useState(null);
  const [active, setActive] = useState(null);
  const [show, setShow] = useState(false);
  const load = () => api.get("/tickets").then(({ data }) => setTickets(data)).catch(() => setTickets([]));
  useEffect(() => { load(); }, []);
  const open = async (t) => { const { data } = await api.get(`/tickets/${t.id}`); setActive(data); };

  if (active) return <Thread ticket={active} onBack={() => { setActive(null); load(); }} isAdmin={false} />;

  return (
    <div className="space-y-6" data-testid="tickets-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Assistenza</h1>
          <p className="text-muted-foreground mt-1 text-sm">Consigli, bug e richieste. Qui vedi solo le tue segnalazioni e le risposte che ricevi.</p>
        </div>
        <button onClick={() => setShow(true)} className="bg-[#E3FF00] text-neutral-900 font-semibold px-4 py-2.5 rounded-lg hover:brightness-95 flex items-center gap-2 shrink-0" data-testid="ticket-new-button"><Plus className="h-4 w-4" /> Nuova segnalazione</button>
      </div>
      <div className="bg-card rounded-xl border border-border/80 divide-y divide-border/60" data-testid="tickets-list">
        {tickets === null ? <div className="p-8 text-center text-muted-foreground">Caricamento...</div>
         : tickets.length === 0 ? <div className="p-10 text-center"><LifeBuoy className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" /><p className="text-sm text-muted-foreground">Nessuna segnalazione. Aprine una!</p></div>
         : tickets.map((t) => (
          <button key={t.id} onClick={() => open(t)} data-testid={`ticket-item-${t.id}`} className="w-full text-left px-4 py-3 hover:bg-muted transition-colors flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-medium truncate flex items-center gap-2">{t.user_unread && <span className="h-2 w-2 rounded-full bg-[#E3FF00] ring-1 ring-neutral-900 shrink-0" />}{t.subject}</div>
              <div className="text-xs text-muted-foreground">{catLabel(t.category)} · {t.messages?.length || 0} messaggi</div>
            </div>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${statusCls(t.status)}`}>{statusLabel(t.status)}</span>
          </button>
        ))}
      </div>
      {show && <NewTicket onClose={() => setShow(false)} onCreated={(t) => { setShow(false); load(); setActive(t); }} />}
    </div>
  );
}

export function NewTicket({ onClose, onCreated }) {
  const [f, setF] = useState({ subject: "", category: "consiglio", message: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.subject.trim() || !f.message.trim()) return toast.error("Compila oggetto e messaggio");
    setSaving(true);
    try { const { data } = await api.post("/tickets", f); toast.success("Segnalazione inviata"); onCreated(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); setSaving(false); }
  };
  const inp = "w-full bg-background border border-input rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card rounded-2xl border border-border shadow-2xl w-full max-w-lg p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="ticket-new-modal">
        <h3 className="font-display text-lg font-semibold">Nuova segnalazione</h3>
        <select className={inp} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="ticket-category-select">
          {NEW_CATS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
        </select>
        <input className={inp} placeholder="Oggetto" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="ticket-subject-input" />
        <textarea className={inp + " min-h-28"} placeholder="Descrivi nel dettaglio..." value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="ticket-message-input" />
        <button onClick={submit} disabled={saving} className="w-full bg-[#E3FF00] text-neutral-900 font-semibold py-2.5 rounded-lg hover:brightness-95 disabled:opacity-60" data-testid="ticket-submit">{saving ? "Invio..." : "Invia segnalazione"}</button>
      </div>
    </div>
  );
}

export function Thread({ ticket, onBack, isAdmin }) {
  const [t, setT] = useState(ticket);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const base = isAdmin ? `/admin/tickets/${t.id}` : `/tickets/${t.id}`;
  const reply = async () => {
    if (!msg.trim()) return;
    setSending(true);
    try {
      await api.post(`${base}/reply`, { message: msg });
      const { data } = await api.get(base);
      setT(data); setMsg("");
    } catch (e) { toast.error("Errore invio"); } finally { setSending(false); }
  };
  const close = async () => { await api.post(`${base}/close`); toast.success("Segnalazione chiusa"); const { data } = await api.get(base); setT(data); };
  return (
    <div className="space-y-4" data-testid="ticket-thread">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={onBack} className="h-9 w-9 rounded-lg border border-border grid place-items-center hover:bg-muted shrink-0" data-testid="ticket-back"><ArrowLeft className="h-4 w-4" /></button>
          <div className="min-w-0">
            <h1 className="font-display text-lg sm:text-xl font-bold truncate">{t.subject}</h1>
            <p className="text-xs text-muted-foreground">{catLabel(t.category)}{isAdmin && ` · ${t.user_name} (${t.user_email})`} · <span className={`px-1.5 py-0.5 rounded ${statusCls(t.status)}`}>{statusLabel(t.status)}</span></p>
          </div>
        </div>
        {isAdmin && t.status !== "closed" && <button onClick={close} className="text-xs text-rose-600 hover:underline shrink-0" data-testid="ticket-close">Chiudi</button>}
      </div>
      <div className="bg-card rounded-xl border border-border/80 p-4 space-y-3 max-h-[52vh] overflow-y-auto">
        {t.messages?.map((m) => (
          <div key={m.id} className={`flex ${m.author === "admin" ? "justify-start" : "justify-end"}`}>
            <div className={`max-w-[80%] rounded-xl px-3.5 py-2.5 text-sm ${m.author === "admin" ? "bg-[#E3FF00] text-neutral-900" : "bg-muted"}`}>
              <div className="text-[10px] font-semibold opacity-70 mb-0.5">{m.author === "admin" ? "Supporto Mypasspartu" : m.author_name}</div>
              <div className="whitespace-pre-wrap">{m.text}</div>
            </div>
          </div>
        ))}
      </div>
      {t.status !== "closed" && (
        <div className="flex gap-2">
          <input className="flex-1 bg-background border border-input rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring" placeholder="Scrivi una risposta..." value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && reply()} data-testid="ticket-reply-input" />
          <button onClick={reply} disabled={sending} className="bg-[#E3FF00] text-neutral-900 font-semibold px-4 py-2.5 rounded-lg hover:brightness-95 flex items-center gap-2 disabled:opacity-60" data-testid="ticket-reply-send"><Send className="h-4 w-4" /> Invia</button>
        </div>
      )}
    </div>
  );
}
